import { useMemo, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";
import Spinner from "@atlaskit/spinner";

import { Adg4Button } from "@/components/adg4/button/src";
import { Row, TypographyStyles } from "@/libraries/wpl/foundations/src";

import {
    CustomerStoriesGrid,
    CustomerStoryNoResultsFallback
} from "../components";
import {
    CustomerStoriesSectionProps,
    StoryGrid,
    getNumberOfCustomerStoryColumns,
    sortStoriesByHeight,
    useWindowDimensions
} from "../utils";

export const CustomerStoriesSection = ({
    totalNumberOfCustomerStories,
    customerStoryList,
    fallbackCustomerStoryList,
    filterSearchHasRun,
    loadMoreStories
}: CustomerStoriesSectionProps) => {
    const intl = useIntl();
    const [numStories, setNumStories] = useState(0);

    const { width } = useWindowDimensions();
    const numColumns = getNumberOfCustomerStoryColumns(width);

    let storyGrid: StoryGrid = useMemo(() => {
        return {};
    }, []);

    storyGrid = useMemo(() => {
        if (numStories !== customerStoryList.length) {
            setNumStories(customerStoryList.length);
        }

        // if the number of columns has changed, re-sort the stories
        if (numColumns !== Object.keys(storyGrid).length) {
            return sortStoriesByHeight(
                storyGrid,
                customerStoryList.slice(0, numStories),
                numColumns
            );
            // if the number of stories has changed, append the new stories to the grid
        } else {
            return sortStoriesByHeight(
                storyGrid,
                customerStoryList.slice(0, customerStoryList.length),
                numColumns
            );
        }
        return storyGrid;
    }, [numStories, storyGrid, customerStoryList, numColumns]);

    return (
        <UFOSegment name="customers-page-stories-grid">
            <TypographyStyles>
                <div css={customerStoryContainerStyles}>
                    {customerStoryList.length === 0 && filterSearchHasRun ? (
                        <CustomerStoryNoResultsFallback
                            numColumns={numColumns}
                            fallbackCustomerStories={fallbackCustomerStoryList}
                        />
                    ) : customerStoryList.length === 0 &&
                      !filterSearchHasRun ? (
                        <Row layout={"12"}>
                            <div css={spinnerStyles}>
                                <Spinner size="xlarge" />
                            </div>
                        </Row>
                    ) : (
                        <CustomerStoriesGrid
                            numColumns={numColumns}
                            storiesGrid={storyGrid}
                        />
                    )}
                    {numStories !== 0 &&
                        numStories < totalNumberOfCustomerStories && (
                            <Row layout="12">
                                <div css={loadMoreStyles}>
                                    <p>
                                        <FormattedMessage
                                            id="CustomersLandingPage.showingListOfStories"
                                            defaultMessage="Showing <numberOfCustomerStories></numberOfCustomerStories> of <totalNumberOfCustomerStories></totalNumberOfCustomerStories> stories"
                                            values={{
                                                numberOfCustomerStories: () =>
                                                    customerStoryList.length,
                                                totalNumberOfCustomerStories:
                                                    () =>
                                                        totalNumberOfCustomerStories
                                            }}
                                        />
                                    </p>
                                    <Adg4Button
                                        variant="secondary"
                                        color="black"
                                        onClick={loadMoreStories}
                                        dataEventProps={{
                                            "data-event": "clicked",
                                            "data-event-component": "button",
                                            "data-event-container":
                                                "customerStorySection",
                                            "data-product-key": "customers",
                                            "data-label-english":
                                                "Load more stories"
                                        }}
                                    >
                                        {intl.formatMessage({
                                            id: "CustomersLandingPage.loadMoreStories",
                                            defaultMessage: "Load more stories"
                                        })}
                                    </Adg4Button>
                                </div>
                            </Row>
                        )}
                </div>
            </TypographyStyles>
        </UFOSegment>
    );
};

const customerStoryContainerStyles = css({
    marginBlockEnd: "4rem"
});

const spinnerStyles = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    marginBlockStart: "160px",
    height: "80vh"
});

const loadMoreStyles = css({
    margin: "32px auto 0",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
});
